import classNames from "classnames";
import { useEffect, useState } from "react";
import ReactSlider from "react-slider";
import { Icon } from "~/components/ui/core/Icon";
import { type PercentileBounds } from "~/services/percentiles";

type Event = {
  target: {
    name?: string;
    value: PercentileBounds;
  };
};

type Props = {
  name?: string;
  value: PercentileBounds;
  disabled?: boolean;
  onChange: (event: Event) => void;
};

export const PercentilesSlider: React.FC<Props> = ({ name, value, disabled, onChange }) => {
  const [state, setState] = useState(value);

  useEffect(() => {
    setState(value);
  }, [value]);

  return (
    <ReactSlider
      pearling
      ariaLabel={["Lower percentile", "Middle percentile", "Higher percentile"]}
      className="relative flex h-3 w-full items-center rounded-full border border-gray-300 bg-gradient-to-br from-gray-200 to-gray-300"
      disabled={disabled}
      max={90}
      min={10}
      renderThumb={(props, state) => {
        return (
          <div
            {...props}
            className="focus:ring-offset absolute z-10 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-primary-600 bg-gradient-to-br from-primary-400 to-primary-600 hover:border-primary-700 hover:from-primary-600 hover:to-primary-600 focus:border-primary-700 focus:from-primary-600 focus:to-primary-600 focus:outline-none focus:ring focus:ring-primary-300"
            style={{ left: `calc(${state.valueNow}% - 1rem)`, willChange: "left" }}
          >
            <span className="text-xs font-medium text-white">P{state.valueNow}</span>
            <div className="absolute inset-x-0 -bottom-4">
              {state.index === 0 && (
                <div className="flex w-full items-center justify-center bg-white">
                  <Icon className="text-center text-gray-400" name="arrow-down" size="sm" />
                </div>
              )}
              {state.index === 1 && (
                <div className="flex w-full items-center justify-center bg-white">
                  <Icon className="text-center text-gray-400" name="average" size="sm" />
                </div>
              )}
              {state.index === 2 && (
                <div className="flex w-full items-center justify-center bg-white">
                  <Icon className="text-center text-gray-400" name="arrow-up" size="sm" />
                </div>
              )}
            </div>
          </div>
        );
      }}
      renderTrack={(props, state) => {
        const value = (state.value as number[])[state.index - 1] ?? 0;
        const nextValue = (state.value as number[])[state.index] as number;
        const excluded = state.index === 0 || state.index === 3;

        return (
          <div
            key={state.index}
            className={classNames({
              "absolute inset-y-0 -my-px transition-all duration-75": true,
              "border-t border-b border-primary-600 bg-primary-300": !excluded,
            })}
            style={{
              left: `calc(${value}% - 1rem + 1px)`,
              width: `calc(${nextValue - value}% + 1px)`,
              willChange: "left, width",
            }}
          />
        );
      }}
      step={5}
      value={[state.low * 100, state.middle * 100, state.high * 100]}
      onAfterChange={(value) => {
        const values = value as [number, number, number];

        onChange({
          target: {
            name,
            value: {
              low: values[0] / 100,
              middle: values[1] / 100,
              high: values[2] / 100,
            },
          },
        });
      }}
      onChange={(value) => {
        const values = value as [number, number, number];

        setState({
          low: values[0] / 100,
          middle: values[1] / 100,
          high: values[2] / 100,
        });
      }}
    />
  );
};
